import { Alert, BrandMark, Button, Field } from "@artisan/ui";
import { LogIn } from "lucide-react";
import { type FormEvent, useState } from "react";

import { AuthError, login, loginMfa } from "../lib/auth";
import { InstallHint } from "../components/install-hint";
import { appBase, webUrl } from "../lib/env";

const MESSAGES: Record<string, string> = {
  invalid_credentials: "И-мэйл эсвэл нууц үг буруу байна.",
  invalid_mfa_code: "Код буруу байна. Дахин оролдоно уу.",
  invalid_mfa_token: "Хугацаа дууссан. Дахин нэвтэрнэ үү.",
  rate_limited: "Хэт олон оролдлого. Хэдэн минут хүлээнэ үү.",
};

export function LoginScreen({ expired }: { expired?: boolean }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const forgot = webUrl("/forgot-password");
  const signup = webUrl("/signup");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfa) await loginMfa(mfa, code);
      else {
        const r = await login(email.trim(), password);
        if (r.kind === "mfa") setMfa(r.mfa_token);
      }
    } catch (err) {
      setError(
        err instanceof AuthError
          ? (MESSAGES[err.code] ?? "Нэвтэрч чадсангүй. Дахин оролдоно уу.")
          : "Сүлжээ алга. Интернэтээ шалгаад дахин оролдоно уу.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid h-full grid-cols-[1.1fr_1fr] bg-background">
      <div className="relative overflow-hidden bg-sidebar">
        <img src={`${appBase}login-kitchen.jpg`} alt="" className="absolute inset-0 size-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
        <div className="relative flex h-full flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <BrandMark size={40} labelled={false} />
            <span className="font-display text-xl font-semibold tracking-tight">Artisan</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-semibold tracking-tight">Хэмжээд, батлуулаад, үйлдвэрт илгээ.</h1>
            <p className="mt-3 text-base text-white/80">
              Айлд очиж хана, нээлхий, инженерийн цэгийг хэмжиж, загвараа render-ээр батлуулна. Интернэтгүй үед ч бүх зүйл
              хадгалагдана.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" aria-label="Нэвтрэх">
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight">{mfa ? "Хоёр алхамт баталгаажуулалт" : "Нэвтрэх"}</h2>
            <p className="mt-1 text-muted-foreground">
              {mfa ? "Authenticator апп дахь 6 оронтой код эсвэл сэргээх кодоо оруулна уу." : "Artisan бүртгэлээрээ нэвтэрнэ үү."}
            </p>
          </div>
          {expired && !error ? <Alert variant="warning">Нэвтрэлтийн хугацаа дууссан. Дахин нэвтэрнэ үү.</Alert> : null}
          {error ? <Alert variant="destructive">{error}</Alert> : null}
          {mfa ? (
            <Field label="Код" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" autoFocus required />
          ) : (
            <>
              <Field label="И-мэйл" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
              <Field
                label="Нууц үг"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </>
          )}
          <Button type="submit" size="lg" className="w-full" icon={LogIn} disabled={busy}>
            {busy ? "Түр хүлээнэ үү…" : mfa ? "Баталгаажуулах" : "Нэвтрэх"}
          </Button>
          {!mfa && (forgot || signup) ? (
            <p className="flex flex-wrap justify-between gap-2 text-sm">
              {forgot ? (
                <a href={forgot} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
                  Нууц үгээ мартсан уу?
                </a>
              ) : null}
              {signup ? (
                <a href={signup} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
                  Бүртгэл үүсгэх
                </a>
              ) : null}
            </p>
          ) : null}
          <InstallHint />
        </form>
      </div>
    </div>
  );
}
